import { Box, Flex, HStack, Spinner, Stack, Tooltip, useDisclosure } from "@chakra-ui/react";
import { format, formatDistanceToNow } from "date-fns";
import CDS from "design-system";

import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useFormAssignmentsQuery } from "../hooks";
import type { CustomForm } from "../types";
import AssignFormPanel from "./AssignFormPanel";

interface RecipientsTabProps {
  form: CustomForm;
}

function StatusPill({ completed }: { completed: boolean }) {
  return (
    <CDS.Tag
      size={CDS.Tag.size.small}
      variant={completed ? CDS.Tag.variant.success : CDS.Tag.variant.neutral}
    >
      {completed ? "Completed" : "Not started"}
    </CDS.Tag>
  );
}

// The task payload only carries the journey id, so resolve the name/start date here.
// Start date disambiguates the same person assigned across two journeys.
function JourneyLabel({ journeyId }: { journeyId: string }) {
  const { data: journey } = useNewHireJourneyDetailQuery(journeyId);
  const name = journey?.newHire?.fullName ?? "Onboarding journey";
  const start = journey?.startDate ? ` · starts ${format(new Date(journey.startDate), "MMM d")}` : "";
  return (
    <CDS.Text12 color="content.tertiary" noOfLines={1}>
      {name}
      {start}
    </CDS.Text12>
  );
}

export default function RecipientsTab({ form }: RecipientsTabProps) {
  const { data: assignments, isLoading } = useFormAssignmentsQuery(form.id);
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (isLoading) {
    return (
      <Flex justify="center" pt={8}>
        <Spinner />
      </Flex>
    );
  }

  const rows = assignments ?? [];

  return (
    <Stack spacing={4} pt={4} maxW="720px">
      {rows.length === 0 ? (
        <Box borderWidth="1px" borderColor="border.subtle" borderRadius="md" px={4} py={8} textAlign="center">
          <CDS.Text14 color="content.tertiary">No one assigned yet.</CDS.Text14>
        </Box>
      ) : (
        <Box borderWidth="1px" borderColor="border.subtle" borderRadius="md" overflow="hidden">
          {rows.map((task, index) => {
            const completed = task.status === "completed";
            const createdAt = task.createdAt ? new Date(String(task.createdAt)) : null;
            return (
              <Flex
                key={task.id}
                align="center"
                gap={4}
                px={4}
                py={3}
                borderTopWidth={index === 0 ? 0 : "1px"}
                borderColor="border.subtle"
              >
                <Stack spacing={0} minW={0} flex="1">
                  <CDS.Text14 fontWeight="bold" noOfLines={1}>
                    {task.assignee?.fullName ?? "Unknown"}
                  </CDS.Text14>
                  {task.assignee?.workEmail && (
                    <CDS.Text12 color="content.tertiary" noOfLines={1}>
                      {task.assignee.workEmail}
                    </CDS.Text12>
                  )}
                </Stack>

                <Stack spacing={0} minW={0} flex="1">
                  <CDS.Text12 fontWeight="medium" noOfLines={1}>
                    {task.name}
                  </CDS.Text12>
                  {task.newHireJourneyId ? (
                    <JourneyLabel journeyId={String(task.newHireJourneyId)} />
                  ) : (
                    <CDS.Text12 color="content.tertiary" noOfLines={1}>
                      No journey
                    </CDS.Text12>
                  )}
                </Stack>

                <HStack spacing={3} flexShrink={0}>
                  <StatusPill completed={completed} />
                  {createdAt && (
                    <Tooltip label={format(createdAt, "MMM d, yyyy 'at' h:mm a")} placement="top" hasArrow>
                      <CDS.Text12 color="content.tertiary" whiteSpace="nowrap" cursor="default">
                        {formatDistanceToNow(createdAt, { addSuffix: true })}
                      </CDS.Text12>
                    </Tooltip>
                  )}
                </HStack>
              </Flex>
            );
          })}
        </Box>
      )}

      <Box alignSelf="flex-start">
        <CDS.Button
          intent={CDS.Button.intent.indigo}
          variant={CDS.Button.variant.outline}
          size="sm"
          onClick={onOpen}
        >
          Assign someone
        </CDS.Button>
      </Box>

      <AssignFormPanel form={form} isOpen={isOpen} onClose={onClose} existingAssignments={rows} />
    </Stack>
  );
}
